import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import classNames from 'classnames';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel/components/Options';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { ImagePresentational } from '../../types/common';
import { EmblaSliderThumbs } from '../EmblaSliderThumbs/EmblaSliderThumbs';
import { Typography, TypographyColor } from '../Typography/Typography';
import { Arrow, Chevron } from '../icons';
import { ArrowColor } from '../icons/Arrow/Arrow';
import { ChevronColor } from '../icons/Chevron/Chevron';
import styles from './EmblaSlider.module.css';

const messages = defineMessages({
  slideSelector: {
    id: 'src.components.EmblaSlider.slideSelector',
    defaultMessage: '{activeSlide} of {totalSlides}',
  },
});

export enum EmblaNavColor {
  dark = 'dark',
  light = 'light',
}

type ColorData = {
  active: ChevronColor;
  inactive: ChevronColor;
};

export const DEFAULT_SETTINGS: Partial<EmblaOptionsType> = {
  align: 'start',
  containScroll: false,
};

export const SCROLL_PROGRESS_THRESHOLD = 0.006;

const NAV_COLORS: {[key in EmblaNavColor]: ColorData } = {
  [EmblaNavColor.dark]: {
    active: ChevronColor.primary,
    inactive: ChevronColor.accent,
  },
  [EmblaNavColor.light]: {
    active: ChevronColor.accentWhite,
    inactive: ChevronColor.accent,
  },
};

export type UseHandleOnSelectParams = {
  emblaApi?: EmblaCarouselType;
  isLoop?: boolean;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number | null>>;
};

type UseHandleOnClickParams = {
  emblaApi?: EmblaCarouselType;
};

type UseCreateEmblaSliderParams = {
  settings?: EmblaOptionsType;
};

export const hooks = {
  useGetStates() {
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [isScrolling, setIsScrolling] = React.useState(false);

    return React.useMemo(() => ({
      canScrollPrev,
      canScrollNext,
      isScrolling,
      setCanScrollPrev,
      setCanScrollNext,
      setIsScrolling,
    }),
    [canScrollPrev, canScrollNext, isScrolling]);
  },

  useCreateEmblaSlider({ settings }: UseCreateEmblaSliderParams) {
    const [emblaRef, emblaApi] = useEmblaCarousel({
      ...DEFAULT_SETTINGS,
      ...settings,
    });

    const selectedSlide = emblaApi?.selectedScrollSnap();

    const handlePrevSlide = React.useCallback(() => {
      emblaApi?.scrollPrev();
    }, [emblaApi]);

    const handleNextSlide = React.useCallback(() => {
      emblaApi?.scrollNext();
    }, [emblaApi]);

    return {
      emblaRef,
      emblaApi,
      selectedSlide,
      handlePrevSlide,
      handleNextSlide,
    };
  },

  useHandleOnSelect ({
    emblaApi,
    isLoop,
    setSelectedIndex,
  }: UseHandleOnSelectParams) {
    const {
      canScrollPrev,
      canScrollNext,
      isScrolling,
      setCanScrollPrev,
      setCanScrollNext,
      setIsScrolling,
    } = hooks.useGetStates();
    React.useEffect(() => {
      let lastScrollProgress = emblaApi?.scrollProgress();
      const onSelect = () => {
        setCanScrollPrev(isLoop ? true : !!emblaApi?.canScrollPrev());
        setCanScrollNext(isLoop ? true : !!emblaApi?.canScrollNext());
        setSelectedIndex?.(emblaApi?.selectedScrollSnap() ?? null);
      };
      const onScroll = () => {
        setIsScrolling(true);
        checkScrollProgress();
      };
      const checkScrollProgress = () => {
        const currentScrollProgress = emblaApi?.scrollProgress();
        if (currentScrollProgress !== undefined
          && lastScrollProgress !== undefined
          && Math.abs(currentScrollProgress - lastScrollProgress) < SCROLL_PROGRESS_THRESHOLD) {
          setIsScrolling(false);
        } else {
          lastScrollProgress = currentScrollProgress;
        }
      };
      emblaApi?.on('select', onSelect);
      emblaApi?.on('scroll', onScroll);
      onSelect();
      return () => {
        emblaApi?.off('select', onSelect);
        emblaApi?.off('scroll', onScroll);
      };
    }, [
      emblaApi,
      isLoop,
      setCanScrollNext,
      setCanScrollPrev,
      setIsScrolling,
      setSelectedIndex,
    ]);
    return {
      canScrollPrev,
      canScrollNext,
      isScrolling,
    };
  },

  useHandleOnClick ({
    emblaApi,
  }: UseHandleOnClickParams) {
    return React.useCallback(
      (index: number) => () => {
        emblaApi?.scrollTo(index);
      }, [
        emblaApi,
      ],
    );
  },
};

export type Props = {
  children: React.JSX.Element[];
  containerClassName?: string;
  isNavArrows?: boolean;
  isNavArrowsShadowShown?: boolean;
  isNavCaptionsShown?: boolean;
  isNavShown?: boolean;
  isSlideFullWidth?: boolean;
  navColor?: EmblaNavColor;
  navContainerClassName?: string;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  settings?: EmblaOptionsType;
  slideClassName?: string;
  thumbnails?: ImagePresentational[];
};

export const EmblaSlider = React.memo((props: Props) => {
  const {
    children,
    containerClassName,
    isSlideFullWidth,
    isNavArrows,
    isNavArrowsShadowShown,
    isNavCaptionsShown,
    isNavShown,
    thumbnails,
    navContainerClassName,
    navColor = EmblaNavColor.dark,
    settings,
    setSelectedIndex,
    slideClassName,
  } = props;

  const {
    emblaRef,
    emblaApi,
    selectedSlide,
    handlePrevSlide,
    handleNextSlide,
  } = hooks.useCreateEmblaSlider({
    settings,
  });

  const handleOnClick = hooks.useHandleOnClick({ emblaApi });

  const {
    canScrollPrev,
    canScrollNext,
    isScrolling,
  } = hooks.useHandleOnSelect({
    emblaApi,
    isLoop: settings?.loop,
    setSelectedIndex,
  });

  const { isClient } = hydrationHooks.useIsClient();

  return (
    <div className={styles.emblaWrapper}>
      <div ref={emblaRef} className={styles.embla}>
        <div
          className={classNames(styles.emblaContainer, containerClassName)}
        >
          {children.map((slide, index)=> (
            <div
              className={classNames( slideClassName, {
                [styles.slideFullWidth]: isSlideFullWidth,
                [styles.disabled]: !isClient,
              })}
              key={slide.key ?? index}
              onClick={handleOnClick(index)}
            >
              {slide}
            </div>
          ))}
        </div>
        { isNavShown && (
          <div className={classNames(styles.nav, navContainerClassName, {
            [styles.navWithThumbnails]: !!thumbnails,
          })}>
            <button className={classNames(styles.navButtonWrapper, {
              [styles.arrowWrapper]: isNavArrows,
              [styles.arrowWrapperShadow]: isNavArrowsShadowShown,
            })}
            onClick={handlePrevSlide}
            disabled={!canScrollPrev || isScrolling || !isClient}
            >
              { isNavArrows ? (
                <Arrow
                  fill={canScrollPrev ? ArrowColor.primary : ArrowColor.disabled}
                  width={24}
                  height={24}
                  variant='left'
                />
              ) : (
                <Chevron
                  variant='left'
                  fill={canScrollPrev
                    ? NAV_COLORS[navColor].active
                    : NAV_COLORS[navColor].inactive}
                  className={styles.navIcon}
                />
              )}
            </button>

            { (isNavCaptionsShown && typeof selectedSlide == 'number') && (
              <Typography variant='body2' color={TypographyColor.Accent}>
                <FormattedMessage
                  {...messages.slideSelector}
                  values={{
                    activeSlide: selectedSlide + 1,
                    totalSlides: children.length,
                  }}
                />
              </Typography>
            )}

            { thumbnails && (
              <EmblaSliderThumbs
                emblaApi={emblaApi}
              >
                {thumbnails}
              </EmblaSliderThumbs>
            )}

            <button className={classNames(styles.navButtonWrapper, {
              [styles.arrowWrapper]: isNavArrows,
              [styles.arrowWrapperShadow]: isNavArrowsShadowShown,
            })}
            onClick={handleNextSlide}
            disabled={!canScrollNext || isScrolling || !isClient}
            >
              { isNavArrows ? (
                <Arrow
                  fill={canScrollNext ? ArrowColor.primary : ArrowColor.disabled}
                  width={24}
                  height={24}
                  variant='right'
                />
              ) : (
                <Chevron
                  variant='right'
                  fill={canScrollNext
                    ? NAV_COLORS[navColor].active
                    : NAV_COLORS[navColor].inactive}
                  className={styles.navIcon}
                />
              )}
            </button>

          </div>
        )}
      </div>
    </div>
  );
});
